/**
 * Age verification functionality
 * Prevents access to the site until user confirms they are 18+
 * Uses sessionStorage to remember the choice for the current browser session
 */

const SESSION_STORAGE_KEY = 'age_verified';

/**
 * Check if the user has already verified their age in this session
 */
function isAgeVerified() {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  } catch (error) {
    // In case sessionStorage is not available, show the modal
    return false;
  }
}

/**
 * Handle when user confirms they are 18+
 */
function confirmAge() {
  try {
    // Save the verification to sessionStorage
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  } catch (error) {
    console.warn('Could not save age verification to sessionStorage');
  }
  
  // Hide the modal
  const modal = document.getElementById('age-verification-modal');
  if (modal && 'closeDialog' in modal && typeof modal.closeDialog === 'function') {
    modal.closeDialog();
  }
  
  // Dispatch custom event for tracking/analytics if needed
  document.dispatchEvent(new CustomEvent('age-verified', {
    bubbles: true,
    detail: { verified: true }
  }));
}

/**
 * Handle when user indicates they are not 18+
 */
function denyAge() {
  // Dispatch event first
  document.dispatchEvent(new CustomEvent('age-verification-denied', {
    bubbles: true,
    detail: { verified: false }
  }));
  
  // Redirect to an external site
  window.location.href = 'https://www.google.com';
}

/**
 * Handle button clicks in the modal
 * @param {MouseEvent} event
 */
function handleButtonClick(event) {
  if (!event.target) return;
  
  const button = /** @type {Element} */ (event.target).closest('[data-age-verify]');
  if (!button) return;

  const action = /** @type {HTMLElement} */ (button).dataset.ageVerify;
  
  if (action === 'confirm') {
    confirmAge();
  } else if (action === 'deny') {
    denyAge();
  }
}

/**
 * Initialize age verification when DOM is ready
 */
function initializeAgeVerification() {
  // Check if user has already verified their age in this session
  if (isAgeVerified()) {
    return; // Don't show modal if already verified
  }
  
  const modal = document.getElementById('age-verification-modal');
  if (!modal) {
    console.warn('Age verification modal not found');
    return;
  }
  
  // Add event listener for button clicks
  modal.addEventListener('click', handleButtonClick);
  
  // Show the modal immediately
  if ('showDialog' in modal && typeof modal.showDialog === 'function') {
    modal.showDialog();
  }
  
  // Prevent closing on outside click and escape key
  const dialog = modal.querySelector('dialog');
  if (dialog) {
    dialog.addEventListener('click', (event) => {
      // Prevent closing when clicking on the backdrop
      if (event.target === dialog) {
        event.stopPropagation();
      }
    });
    
    dialog.addEventListener('keydown', (event) => {
      // Prevent closing with escape key
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    });
  }
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAgeVerification);
} else {
  // DOM is already loaded
  initializeAgeVerification();
}
